/* ============================================================
   Shopify Wishlist - LocalStorage Version
   Author: RF Tech
   Description:
   - Handles wishlist across product cards, quick view, and product pages.
   - Stores product data from JSON script tags next to wishlist buttons.
   - Uses localStorage, custom events, and mutation observers.
   ============================================================ */

(function() {
    const STORAGE_KEY = 'wishlist_items';
    const EVENT_NAMESPACE = 'wishlist';
    const WISHLIST_BTN = '.nowish-btn';
    const WISHLIST_COUNT_SELECTOR = ".nowish-count";
    const WISHLIST_GRID_SELECTOR = "[data-wishlist-grid]";
    const WISHLIST_DRAWER_SELECTOR = ".nowish-count-wrapper";
    const WISHLIST_REMOVE_SELECTOR = ".wishlist-remover";
    const WISHLIST_VARIANT_SELECT = "#variant-selector";
    const WISHLIST_CARD_SELECTOR = "vertical-product-card";

    // ---------------------------
    // Utilities
    // ---------------------------

    const getWishlist = () => {
        try {
            return JSON.parse(localStorage.getItem(STORAGE_KEY)) || [];
        } catch (err) {
            console.error('[Wishlist] Failed to parse localStorage:', err);
            return [];
        }
    };

    const saveWishlist = (list) => {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
        //console.log('[Wishlist] Saved wishlist:', list);
    };

    const isInWishlist = (id) => {
        const wishlist = getWishlist();
        return wishlist.some((item) => String(item.id) === String(id));
    };

   const addToCart = async (id) => {
      try {
        const response = await fetch("/cart/add.js", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({
            id,   // variant ID
            quantity: 1,
          }),
        });

        if (!response.ok) throw new Error("Failed to add to cart");

        const data = await response.json();

        // ✅ Trigger your notification
        if (window.showNotification) {
          document.querySelector('#GlobalNotification')?.classList?.add('is-visible');
          window.showNotification(`"${data.title}" added to cart!`, "success", 4000);
        }

        // Optional: You could update cart UI here if needed
        //console.log("Added to cart:", data);

        document.querySelector('#CartDrawer').update();

      } catch (error) {
        console.error("Error adding to cart:", error);
        if (window.showNotification) {
          window.showNotification("Failed to add item to cart.", "error", 4000);
        }
      }
    };

    // ---------------------------
    // Core Wishlist Actions
    // ---------------------------

    const addToWishlist = (product) => {
        const wishlist = getWishlist();
        if (!isInWishlist(product.id)) {
            wishlist.unshift(product);
            saveWishlist(wishlist);
            document.dispatchEvent(new CustomEvent('addToWishlist', {
                detail: product
            }));
            updateWishlistButtons();
            //window.showNotification('Item added to wishlist!', 'success', 4000);
            //console.log('[Wishlist] Added:', product);
        }
    };

    const removeFromWishlist = (id) => {
        let wishlist = getWishlist();
        const removed = wishlist.find((p) => String(p.id) === String(id));
        wishlist = wishlist.filter((p) => String(p.id) !== String(id));
        saveWishlist(wishlist);
        document.dispatchEvent(new CustomEvent('removeFromWishlist', {
            detail: removed
        }));
        updateWishlistButtons();
        //console.log('[Wishlist] Removed:', removed);
    };

    // ---------------------------
    // UI Update Functions
    // ---------------------------

    const updateWishlistButtons = () => {
        //console.log('[Wishlist] Updating button states...');
        document.querySelectorAll('.wishlist-btn').forEach((btn) => {
            const id = btn.dataset.productId || btn.dataset.id;
            if (isInWishlist(id)) {
                btn.classList.add('is-added');
            } else {
                btn.classList.remove('is-added');
            }
        });
    };

    const updateWishlistCount = () => {
        const wishlist = getWishlist();
        const countEls = document.querySelectorAll(WISHLIST_COUNT_SELECTOR);
        countEls.forEach((el) => {
            const count = wishlist.length;
            el.textContent = count;
            if (count > 0) {
                el.style.display = 'flex';
            } else {
                el.style.display = 'none';
            }
        });
    };

    const updateWishlistRemovers = () => {
      const grid = document.querySelector(WISHLIST_GRID_SELECTOR);
      grid.querySelectorAll(WISHLIST_REMOVE_SELECTOR).forEach((btn) => {
        btn.addEventListener("click", function(){
            const id = btn.dataset.productId;
            if(id){
                removeFromWishlist(id)
            }
        });   
        });
    }

    const updateWishlistSelects = () => {
       const grid = document.querySelector(WISHLIST_GRID_SELECTOR); 
       grid.querySelectorAll(WISHLIST_VARIANT_SELECT).forEach((select) => {
         if(select){
            select.addEventListener('change', function(e){
                const value = e.target.value;
                if(value){
                  addToCart(e.target.value)
                }
            });
         }
       });
    }
    // ---------------------------
    // Button Click Handler
    // ---------------------------

    const handleButtonClick = (btn) => {
        const id = btn.dataset.productId || btn.dataset.id;
        const dataEl = document.querySelector(`#wishlist-data-${id}`);

        if (!dataEl) {
            console.warn('[Wishlist] No JSON data found for product ID:', id);
            return;
        }

        try {
            const product = JSON.parse(dataEl.textContent);
            if (isInWishlist(product.id)) {
                removeFromWishlist(product.id);
            } else {
                addToWishlist(product);
            }
            updateWishlistButtons();
            updateWishlistCount();
        } catch (err) {
            console.error('[Wishlist] Failed to parse product JSON:', err);
        }
    };

    function formatAmount (amount) {
        return window.Neverover.formatPrice(amount);
    }
    // ---------------------------
    // Wishlist Renderer
    // ---------------------------

    function renderProductBadges(badges = null) {
      if (!badges || !badges.length) return "";

      let html = `<div class="product-card__on-image-badges">`;

      badges.forEach(badge => {
        html += `
          <div class="${badge?.class} product-badge product-badge--custom-1">
            ${badge?.text}
          </div>
        `;
      });

      html += `</div>`;
      return html;
    }


    const renderWishlist = async () => {
        //console.log('[Wishlist] Rendering wishlist grid...');
        const wishlist = getWishlist();
        const container = document.querySelector(WISHLIST_GRID_SELECTOR);
        const loader = container?.querySelector(".wishlist-loader");
        const itemsContainer = container?.querySelector(".wishlist-items");
        if (!container) {
            console.warn('[Wishlist] No wishlist grid container found.');
            updateWishlistCount();
            return;
        }

        loader.style.display = "flex";
        itemsContainer.innerHTML = "";

        if (wishlist.length === 0) {
            itemsContainer.innerHTML = `<p class="empty-wishlist">Your wishlist is empty.</p>`;
            loader.style.display = "none";
            return;
        }

        itemsContainer.innerHTML = wishlist
    .map(
      (p) => `
<vertical-product-card class="vertical-product-card vertical-product-card--inside-block-with-background" data-url="${p.url}" data-id="${p.id}">
  <div class="vertical-product-card__inner shape shape--default">
    <a href="${p.url}" class="vertical-product-card__link" aria-label="${p.title}"></a>
    <div class="vertical-product-card__image-wrapper">
      <img class="vertical-product-card__image shape__target-image" alt="${p.title}" src="${p.image}" loading="lazy">
    </div>

    <div class="vertical-product-card__buttons vertical-product-card__buttons--visible-on-hover">
      <div id="product-card-quick-view-btn-${p.id}">
        <button class="btn btn--solid btn--md vertical-product-card__button" data-quick-view-button="" data-product-card-quick-view-button="" data-quick-view-id="Quick-view" aria-label="Quick view" type="button" data-product-id="${p.id}" aria-label="Quick view" type="button">
          <span>
            <svg class="vertical-product-card__button-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 26" fill="none">
              <path d="M16.9999 8.88672C16.9999 6.1253 14.7613 3.88672 11.9999 3.88672C9.2385 3.88672 6.99992 6.1253 6.99992 8.88672M9.86902 21.8867H14.1308C15.6961 21.8867 16.4787 21.8867 17.1421 21.6765C18.2527 21.3246 19.1835 20.5558 19.7388 19.5316C20.0705 18.9199 20.2183 18.1513 20.5139 16.6142C20.931 14.4453 21.1395 13.3608 20.9612 12.487C20.6618 11.0199 19.6524 9.79786 18.2683 9.22683C17.4439 8.88672 16.3395 8.88672 14.1308 8.88672H9.86902C7.66031 8.88672 6.55596 8.88672 5.73156 9.22683C4.34744 9.79786 3.33799 11.0199 3.03862 12.487C2.86032 13.3608 3.06887 14.4453 3.48598 16.6142C3.78158 18.1513 3.92938 18.9199 4.26106 19.5316C4.81635 20.5558 5.74717 21.3246 6.85775 21.6765C7.52112 21.8867 8.30375 21.8867 9.86902 21.8867Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path>
            </svg>
          </span>
          <div class="loading-spinner btn__loading-spinner"></div>
        </button>
      </div>
    </div>
    
    <div class="wishlist-remove-wrapper wishlist-btn nowish-btn nowish-type-product-card is-added wishlist-remover" data-product-id="${p.id}" data-product-handle="${p.handle}" aria-label="Add to Wishlist" data-wishlist-init="true">
    <svg viewBox="0 0 256 256" width="20" class="icon-wishlist" xmlns="http://www.w3.org/2000/svg">
        <rect fill="none" height="256" width="256"></rect>
        <path d="M128,216S28,160,28,92A52,52,0,0,1,128,72h0A52,52,0,0,1,228,92C228,160,128,216,128,216Z" fill="none" stroke="#000" stroke-linecap="round" stroke-linejoin="round" stroke-width="16"></path>
        </svg>
    </div>
    ${renderProductBadges(p.badges)}
  </div>

  <div class="vertical-product-card__info vertical-product-card__info--with-bottom-spacing">
    <a class="vertical-product-card__title" href="${p.url}" aria-label="${p.title}">${p.title}</a>
    <div class="new-content-list">
      ${
        p.category
          ? `<a class="unstyled-link category-name body-color" href="${p.url}">
              <span>${p.category}${
              p.available_colors?.length ? ", " + p.available_colors.join(", ") : ""
            }</span>
            </a>`
          : ""
      }
      ${
        p.color_swatches?.length
          ? `<div class="color-circles-list">
              ${p.color_swatches
                .map(
                  (sw) => `
              <a href="${sw.url}" class="color-circle ${sw.active == "true" ? 'active': ''}" style="background:${sw.color};--color:${sw.color};" title="${sw.label}">
                <span class="visually-hidden">${sw.label}</span>
              </a>`
                )
                .join("")}
             ${p.color_swatches_count !== "" ? `<span class="extra-color-item">${p.color_swatches_count}</span>`: ""}
            </div>`
          : ""
      }
    </div>

    <div class="vertical-product-card__price">
      <div class="vertical-product-card__price-item">
        <span class="money">${formatAmount(p.price)}</span>
      </div>
    </div>

    ${p.sizes?.length
        ? `<div class="size-list-wrapper body-color">
            <span>Available in </span>
            ${p.sizes.map((s) => `<span data-sizes="">${s}</span>`).join("")}
          </div>`
        : ""
    }
    ${p?.size_variants ? `
  <div class="vertical-product-card__variant-item">
    <span>Size </span>
    <select name="size" id="variant-selector">
      ${p.size_variants
        .map(
          (option) => `<option value="${option.variantId}" ${option.available === "false" ? "disabled" : ""}>${option.label}</option>`
        )
        .join("")}
    </select>
  </div>` : ''}
  </div>
</vertical-product-card>`
    )
    .join("");
        loader.style.display = "none";
        updateWishlistCount();
        updateWishlistRemovers();
        updateWishlistSelects();
        // document.dispatchEvent(new CustomEvent('renderWishlist', {
        //     detail: wishlist
        // }));
    };

    // ---------------------------
    // Mutation Observer
    // ---------------------------

    const observer = new MutationObserver(() => {
        //console.log('[Wishlist] DOM changed - reinitializing buttons...');
        initButtons();
    });

    const observeDOM = () => {
        observer.observe(document.body, {
            childList: true,
            subtree: true,
        });
    };

    // ---------------------------
    // Initialization
    // ---------------------------

    const initButtons = () => {
        document.querySelectorAll(WISHLIST_BTN).forEach((btn) => {
            if (btn.dataset.wishlistInit) return; // prevent duplicate binding
            btn.dataset.wishlistInit = 'true';
            btn.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                handleButtonClick(btn)
            });
        });
        updateWishlistButtons();
    };

    const initListeners = () => {
        // Custom manual triggers
        document.addEventListener('renderWishlist', renderWishlist);
        document.addEventListener('triggerWishlist', renderWishlist);
        document.addEventListener('removeFromWishlist', renderWishlist);

        // Listen for remove button in wishlist grid
        document.body.addEventListener('click', (e) => {
            if (e.target.classList.contains('wishlist-remove')) {
                e.preventDefault();
                e.stopPropagation();
                const id = e.target.dataset.id;
                removeFromWishlist(id);
                renderWishlist();
            }
        });
    };

    // ---------------------------
    // Initialize on Load
    // ---------------------------

    //   document.addEventListener('DOMContentLoaded', () => {
    //     console.log('[Wishlist] Initializing...');
    //     initButtons();
    //     initListeners();
    //     observeDOM();
    //   });

    window.addEventListener('load', () => {
        //console.log('[Wishlist] Initializing on window.load...');
        updateWishlistCount();
        initButtons();
        initListeners();
        observeDOM();
        //console.log('[Wishlist] Ready ✅');
    });
    const wishlistAPI = {
      getWishlist,
      saveWishlist,
      isInWishlist,
      addToWishlist,
      removeFromWishlist,
      renderWishlist,
      addToCart,
      updateWishlistCount,
      updateWishlistButtons
    };

    // expose and freeze
    window.wishlist = Object.freeze(window.wishlist || wishlistAPI);
    updateWishlistCount();
})();